"use client";

import React, { useMemo } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Banknote, Bed, Landmark, SlidersHorizontal } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { twMerge } from "tailwind-merge";
import { ListingFilters } from "@/lib/fetch/listing";
import FilterRange from "./FilterRange";
import FilterChoice from "./FilterChoice";
import FilterCombobox from "./FilterCombobox";
import FilterPropertyType from "./FilterPropertyType";
import { getAllLocationCityOptions } from "@/lib/data/location/data";

type Props = {
  filters: ListingFilters;
  setFilters: (filters: ListingFilters) => void;
  countFilters: number;
  /** Full filter panel rendered inside the Sheet. */
  children: React.ReactNode;
};

/**
 * Mobile-only (<lg) quick-filter chip row. The Filters chip opens the full
 * Sheet; the other chips edit their own filter inline via a Popover and show
 * the currently selected value.
 */
const SearchFilterChips: React.FC<Props> = ({
  filters,
  setFilters,
  countFilters,
  children,
}) => {
  const t = useTranslations("search");
  const tL = useTranslations("listing");
  const locale = useLocale();
  const cities = useMemo(() => getAllLocationCityOptions(locale), [locale]);

  const rangeLabel = (
    fallback: string,
    unit: string,
    min?: number,
    max?: number,
  ) => {
    if (min !== undefined && max !== undefined)
      return `${unit}${min} - ${unit}${max}`;
    if (min !== undefined) return `${unit}${min}+`;
    if (max !== undefined) return `≤ ${unit}${max}`;
    return fallback;
  };

  const priceActive =
    filters.priceMin !== undefined || filters.priceMax !== undefined;
  const priceLabel = rangeLabel(
    t("quick-filter.price"),
    "€",
    filters.priceMin,
    filters.priceMax,
  );

  const typeActive = !!filters.type?.length;
  const typeLabel = typeActive
    ? `${tL(`attributes.PROPERTY_TYPE.${filters.type![0]}`)}${
        filters.type!.length > 1 ? ` +${filters.type!.length - 1}` : ""
      }`
    : t("quick-filter.property-type");

  const roomsActive =
    filters.bedroomsMin !== undefined || filters.bedroomsMax !== undefined;
  const roomsLabel = roomsActive
    ? `${rangeLabel("", "", filters.bedroomsMin, filters.bedroomsMax)} ${tL(
        "attribute_title.BEDROOMS",
      ).toLowerCase()}`
    : t("quick-filter.rooms");

  const cityActive = !!filters.city && filters.city !== "ANY";
  const cityLabel = cityActive
    ? cities.find((city) => city.value === filters.city)?.label || filters.city!
    : t("quick-filter.city");

  return (
    <div className="google-anno-skip sticky top-16 z-30 -mx-2 bg-background/95 px-2 py-2 backdrop-blur lg:hidden">
      <div className="flex flex-row gap-2 overflow-x-auto p-0.5 scrollbar-none">
        <Sheet>
          <SheetTrigger
            aria-label={t("filters")}
            className="flex flex-none flex-row items-center gap-1.5 rounded-full border border-primary bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground"
          >
            <SlidersHorizontal className="size-3.5" />
            {t("filters")}
            {countFilters > 0 && (
              <span className="rounded-full bg-secondary px-1.5 text-[10px] font-bold text-secondary-foreground">
                {countFilters}
              </span>
            )}
          </SheetTrigger>
          <SheetContent
            side="left"
            className="w-4/5 overflow-y-auto scrollbar-none"
          >
            <SheetHeader className="sr-only">
              <SheetTitle>{t("filters")}</SheetTitle>
            </SheetHeader>
            {children}
          </SheetContent>
        </Sheet>

        <QuickChip label={priceLabel} active={priceActive}>
          <FilterRange
            icon={<Banknote className="size-4" />}
            title={tL("attribute_title.PRICE")}
            unit="€"
            min={filters.priceMin}
            max={filters.priceMax}
            setMin={(value) => setFilters({ ...filters, priceMin: value })}
            setMax={(value) => setFilters({ ...filters, priceMax: value })}
            clear={() =>
              setFilters({
                ...filters,
                priceMin: undefined,
                priceMax: undefined,
              })
            }
          />
        </QuickChip>

        <QuickChip label={typeLabel} active={typeActive}>
          <FilterPropertyType
            value={filters.type}
            setValue={(value) => setFilters({ ...filters, type: value })}
          />
        </QuickChip>

        <QuickChip label={roomsLabel} active={roomsActive}>
          <FilterChoice
            icon={<Bed className="size-4" />}
            title={tL("attribute_title.BEDROOMS")}
            variant="segmented"
            value={
              filters.bedroomsMin !== undefined
                ? String(filters.bedroomsMin)
                : "ANY"
            }
            setValue={(value) =>
              setFilters({
                ...filters,
                bedroomsMin: value === "ANY" ? undefined : Number(value),
                bedroomsMax: undefined,
              })
            }
            anyValue="ANY"
            anyLabel={tL("attributes.any")}
            options={[1, 2, 3, 4, 5].map((n) => ({
              label: `${n}+`,
              value: String(n),
            }))}
          />
        </QuickChip>

        <QuickChip label={cityLabel} active={cityActive}>
          <FilterCombobox
            icon={<Landmark className="size-4" />}
            title={t("location.city.name")}
            value={filters.city}
            setValue={(city) => setFilters({ ...filters, city })}
            options={cities}
            anyLabel={t("location.city.any")}
            searchPlaceholder={t("combobox.search-city")}
          />
        </QuickChip>
      </div>
    </div>
  );
};

export default SearchFilterChips;

type QuickChipProps = {
  label: string;
  active: boolean;
  children: React.ReactNode;
};

const QuickChip: React.FC<QuickChipProps> = ({ label, active, children }) => {
  return (
    <Popover>
      <PopoverTrigger
        className={twMerge(
          "flex-none rounded-full border px-4 py-2 text-xs font-medium transition-colors",
          active
            ? "border-primary bg-primary text-primary-foreground"
            : "border-border bg-background text-foreground",
        )}
      >
        {label}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-4">
        {children}
      </PopoverContent>
    </Popover>
  );
};
